import { z } from 'zod';
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** Official energy baseline of a project (AFM method, Ordin MMAP 2.490/2024 art. 4(1) g/h/o, art. 13). */
export const EnergyBaseline = z.object({
    id: z.uuid(),
    annual_value: z.number().positive(),
    unit: z.enum(['kWh', 'MWh']),
    source_document: z.string(),
    reference_date: Day,
    notes: z.string().nullable(),
    installed_power_before_kw: z.number().nullable(),
    operating_hours_per_year: z.number(),
    projected_annual_value: z.number().nullable(),
    co2_factor_kg_per_kwh: z.number(),
    min_savings_pct: z.number(),
    created_at: z.string(),
    created_by: z.string().nullable(),
    superseded_at: z.string().nullable(),
});
export const EnergyBaselineCreate = z.object({
    annual_value: z.number().positive().max(1e12),
    unit: z.enum(['kWh', 'MWh']),
    source_document: z.string().trim().min(1).max(500),
    reference_date: Day,
    notes: z.string().max(4000).optional(),
    installed_power_before_kw: z.number().positive().max(1e7).optional(),
    operating_hours_per_year: z.number().positive().max(8784).optional(),
    projected_annual_value: z.number().positive().max(1e12).optional(),
    co2_factor_kg_per_kwh: z.number().min(0).max(2).optional(),
    min_savings_pct: z.number().min(0).max(100).optional(),
});
export const ProjectStatus = z.enum(['planned', 'active', 'monitoring', 'closed']);
export const Project = z.object({
    id: z.uuid(),
    client_id: z.uuid(),
    client_name: z.string(),
    name: z.string(),
    code: z.string().nullable(),
    funding_programme: z.string().nullable(),
    contract_number: z.string().nullable(),
    description: z.string().nullable(),
    start_date: Day.nullable(),
    end_date: Day.nullable(),
    status: ProjectStatus,
    /** the client's default project: holds every device not assigned to another project; cannot be deleted (U20) */
    is_default: z.boolean(),
    /** installed devices that belong to the project */
    devices: z.number().int(),
    /** its serial list (U20): serials listed, and those without a device yet (not installed) */
    serials: z.object({ listed: z.number().int(), not_installed: z.number().int() }),
    baseline: EnergyBaseline.nullable(),
    can_manage: z.boolean(),
});
export const ProjectList = z.object({ items: z.array(Project) });
export const ProjectCreate = z.object({
    client_id: z.uuid(),
    name: z.string().trim().min(1).max(200),
    code: z.string().trim().max(64).optional(),
    funding_programme: z.string().trim().max(300).optional(),
    contract_number: z.string().trim().max(100).optional(),
    description: z.string().max(4000).optional(),
    start_date: Day.optional(),
    end_date: Day.optional(),
    status: ProjectStatus.optional(),
});
export const ProjectUpdate = ProjectCreate.omit({ client_id: true })
    .partial()
    .extend({
    code: z.string().trim().max(64).nullable().optional(),
    funding_programme: z.string().trim().max(300).nullable().optional(),
    contract_number: z.string().trim().max(100).nullable().optional(),
    description: z.string().max(4000).nullable().optional(),
    start_date: Day.nullable().optional(),
    end_date: Day.nullable().optional(),
});
/**
 * Serial lists (U20). A list is loaded as a draft: each serial gets an outcome against the current registry; the
 * exceptions (serial claimed by another client or another, non-default project) need a resolution per serial before
 * the list can be applied. Duplicates and lines without exactly one serial are rejected and reported.
 */
export const SerialOutcome = z.enum([
    /** device of this client, in another project only by default → moves to this project */
    'assign',
    /** already in this project */
    'unchanged',
    /** device in Discovered → adopted directly into this client and project */
    'adopt',
    /** no device with this identifier yet → listed, adopted into the project when it first appears */
    'not_installed',
    /** exception: the device, or the serial's list, belongs to another client */
    'other_client',
    /** exception: the device, or the serial's list, belongs to another non-default project of this client */
    'other_project',
]);
export const SerialResolution = z.enum(['move', 'skip']);
export const SerialDevice = z.object({
    id: z.uuid(),
    name: z.string(),
    lifecycle: z.string(),
    client_id: z.uuid().nullable(),
    client_name: z.string().nullable(),
    project_id: z.uuid().nullable(),
    project_name: z.string().nullable(),
});
export const SerialClaim = z.object({
    project_id: z.uuid().nullable(),
    project_name: z.string().nullable(),
    client_id: z.uuid().nullable(),
    client_name: z.string().nullable(),
});
export const SerialImportItem = z.object({
    serial: z.string(),
    line: z.number().int(),
    outcome: SerialOutcome,
    device: SerialDevice.nullable(),
    /** who holds the serial now (exceptions only) */
    claimed_by: SerialClaim.nullable(),
    /** adopt / other_client: the device reported no position, or a position outside this client's areas */
    warnings: z.array(z.enum(['no_position', 'outside_area'])),
    /** the client whose area contains the device's position, when any */
    position_client: z.string().nullable(),
    /** false: the device cannot leave its client (other devices are reached through it) → only "skip" */
    can_move: z.boolean(),
    resolution: SerialResolution.nullable(),
});
export const SerialRejected = z.object({
    line: z.number().int(),
    raw: z.string(),
    reason: z.enum(['invalid', 'several_serials', 'duplicate', 'ambiguous', 'duplicate_device']),
});
export const SerialImport = z.object({
    id: z.uuid(),
    project_id: z.uuid(),
    status: z.enum(['draft', 'applied', 'cancelled']),
    created_at: z.string(),
    items: z.array(SerialImportItem),
    rejected: z.array(SerialRejected),
    /** listed serials / devices of the project that the new list leaves out: they return to the default project */
    removed: z.array(z.object({ serial: z.string().nullable(), device: SerialDevice.nullable() })),
    /**
     * Serials of 11 characters (a leading 0 lost, e.g. by a spreadsheet): how many there are, how many would match a
     * device with a 0 in front, and whether the user chose to add it (only a leading 0, only to these serials).
     */
    leading_zero: z.object({
        applied: z.boolean(),
        short: z.number().int(),
        matching: z.number().int(),
    }),
    counts: z.object({
        serials: z.number().int(),
        assign: z.number().int(),
        unchanged: z.number().int(),
        adopt: z.number().int(),
        not_installed: z.number().int(),
        exceptions: z.number().int(),
        unresolved: z.number().int(),
        rejected: z.number().int(),
        removed: z.number().int(),
    }),
});
/** POST /api/v1/projects/:id/serial-import: the list as text, one serial per line (pasted, or a TXT / CSV file) */
export const SerialImportCreate = z.object({ text: z.string().min(1).max(2_000_000) });
/**
 * PATCH …/serial-import: resolutions per serial (null clears one); `leading_zero` adds (true) or removes (false) the
 * 0 in front of the 11-character serials — changing it clears the resolutions (the serials change).
 */
export const SerialImportResolve = z.object({
    resolutions: z.record(z.string().max(32), SerialResolution.nullable()).default({}),
    leading_zero: z.boolean().optional(),
});
export const SerialImportResult = z.object({
    listed: z.number().int(),
    assigned: z.number().int(),
    adopted: z.number().int(),
    moved: z.number().int(),
    not_installed: z.number().int(),
    skipped: z.number().int(),
    removed: z.number().int(),
});
/** GET /api/v1/projects/:id/serials: the project's list with the state of each serial */
export const ProjectSerial = z.object({
    serial: z.string(),
    /** installed = its device is in this project; elsewhere = its device left (released, moved); not_installed = no device yet */
    status: z.enum(['installed', 'elsewhere', 'not_installed']),
    device: SerialDevice.nullable(),
    added_at: z.string(),
});
export const ProjectSerialList = z.object({ items: z.array(ProjectSerial) });
/**
 * Energy and savings over a period (GET /api/v1/projects/:id/energy?from&to, also inside reports).
 *  - official savings: baseline (annual Ci prorated to the period) − consumption of the scope, % of the baseline. The
 *    scope's consumption is the measured one extrapolated by the device-day coverage (measured / coverage), and is
 *    computed only when the coverage reaches min_coverage (0.8); below it the status is `insufficient_coverage`;
 *  - dimming indicator (NOT the official savings): consumption at 100 % of the nominal power over the measured light-on
 *    time − measured consumption, on the device-days that have both, for devices with a known nominal power.
 */
export const EnergySavings = z.object({
    from: Day,
    to: Day,
    days: z.number().int(),
    devices_total: z.number().int(),
    devices_with_energy: z.number().int(),
    /** device-days with an energy value / (devices × days), 0–1 */
    coverage: z.number(),
    /** first day with energy data in the scope and period (null = none) */
    data_from: Day.nullable(),
    measured_kwh: z.number(),
    /** measured / coverage — the whole scope's consumption, only when coverage ≥ min_coverage */
    estimated_total_kwh: z.number().nullable(),
    min_coverage: z.number(),
    baseline: z
        .object({
        id: z.uuid(),
        annual_kwh: z.number(),
        period_kwh: z.number(),
        /** ok = savings computed; insufficient_coverage = too little energy data to compare honestly */
        status: z.enum(['ok', 'insufficient_coverage']),
        savings_kwh: z.number().nullable(),
        savings_pct: z.number().nullable(),
        co2_avoided_t: z.number().nullable(),
        min_savings_pct: z.number(),
        meets_minimum: z.boolean().nullable(),
        projected_period_kwh: z.number().nullable(),
        source_document: z.string(),
        reference_date: Day,
    })
        .nullable(),
    dimming: z.object({
        /** device-days with both a light-on time and an energy value, and a known nominal power */
        device_days: z.number().int(),
        full_power_kwh: z.number(),
        measured_kwh: z.number(),
        savings_kwh: z.number(),
        savings_pct: z.number().nullable(),
    }),
    monthly: z.array(z.object({
        month: z.string(),
        measured_kwh: z.number(),
        baseline_kwh: z.number().nullable(),
        devices_with_energy: z.number().int(),
        coverage: z.number(),
    })),
});
//# sourceMappingURL=projects.js.map